import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { DoctorProfileUseForm } from './use-doctor-profile-form';

/** Scalar fields of the unified profile form that render as a plain input. */
export type ProfileTextFieldName =
  | 'lastname'
  | 'firstname'
  | 'phoneNumber'
  | 'orderRegistrationNumber'
  | 'rppsNumber'
  | 'iban'
  | 'bic'
  | 'commissionRate'
  | 'doctolibId';

/**
 * A labelled text input of the unified profile form, the shape shared by
 * every scalar field (identity, order number, banking, tools).
 *
 * @param props.form - the unified profile form
 * @param props.name - the field name
 * @param props.labelKey - i18n key of the label
 * @param props.placeholderKey - i18n key of the placeholder, if any
 * @param props.type - input type, `text` by default
 * @param props.disabled - whether the field is read-only for this viewer
 * @returns the form item
 */
export const ProfileTextField = ({
  form,
  name,
  labelKey,
  placeholderKey,
  type = 'text',
  disabled = false,
}: {
  form: DoctorProfileUseForm;
  name: ProfileTextFieldName;
  labelKey: string;
  placeholderKey?: string;
  type?: 'text' | 'tel' | 'number';
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="grid gap-2">
          <FormLabel>{t(labelKey)}</FormLabel>
          <FormControl>
            <Input
              type={type}
              placeholder={placeholderKey ? t(placeholderKey) : ''}
              {...field}
              value={field.value ?? ''}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
